/**
 * CTA Banner — 4 variants (centered_giant, split_form, card_floating, image_bg)
 */
import type { CtaBannerContent } from '../schemas/cta_banner';
import type { SectionStyle } from '../types/index';
import { Body, Btn, Eyebrow, H2, SectionFrame } from './primitives';

interface CB {
  content: CtaBannerContent;
  style?: SectionStyle;
}

function ctaJustify(
  align: SectionStyle['ctaAlign'] | undefined,
  fallback: 'flex-start' | 'center' | 'flex-end' = 'center',
): 'flex-start' | 'center' | 'flex-end' {
  if (align === 'left') return 'flex-start';
  if (align === 'center') return 'center';
  if (align === 'right') return 'flex-end';
  return fallback;
}

export function CtaBannerCenteredGiant({ content, style }: CB) {
  return (
    <SectionFrame background="surface-alt">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32, alignItems: 'center', textAlign: 'center', maxWidth: 800, margin: '0 auto' }}>
        {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
        <h2
          className="ls-h1"
          style={{
            fontSize: 'clamp(2rem, 4vw + 1rem, 4rem)',
            fontFamily: 'var(--ls-font-heading)',
            whiteSpace: 'pre-wrap',
          }}
        >
          {content.headline}
        </h2>
        {content.subheadline && <Body style={{ fontSize: '1.0625rem' }}>{content.subheadline}</Body>}
        <div className="ls-cta-stack" style={{ justifyContent: ctaJustify(style?.ctaAlign, 'center') }}>
          <Btn variant="primary" href={content.primaryCtaHref ?? '#'}>
            {content.primaryCta}
          </Btn>
          {content.secondaryCta && (
            <Btn variant="ghost" href={content.secondaryCtaHref ?? '#'}>
              {content.secondaryCta}
            </Btn>
          )}
        </div>
      </div>
    </SectionFrame>
  );
}

export function CtaBannerSplitForm({ content, style: _style }: CB) {
  return (
    <SectionFrame background="surface">
      <div className="ls-split-pair">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2 style={{ whiteSpace: 'pre-wrap' }}>{content.headline}</H2>
          {content.subheadline && <Body>{content.subheadline}</Body>}
        </div>
        <form
          onSubmit={(e) => e.preventDefault()}
          style={{
            display: 'flex',
            gap: 8,
            background: 'var(--ls-surface-alt)',
            padding: 8,
            borderRadius: 'var(--ls-radius-md)',
            border: '1px solid var(--ls-border)',
          }}
        >
          <input
            type="email"
            placeholder={content.emailPlaceholder ?? 'you@studio.com'}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              background: 'transparent',
              color: 'var(--ls-text)',
              fontSize: '0.95rem',
              outline: 'none',
            }}
          />
          <Btn variant="primary" href={content.primaryCtaHref ?? undefined}>
            {content.primaryCta}
          </Btn>
        </form>
      </div>
    </SectionFrame>
  );
}

export function CtaBannerCardFloating({ content, style }: CB) {
  return (
    <SectionFrame background="surface">
      <div
        className="ls-card"
        style={{
          background: 'var(--ls-accent)',
          color: 'var(--ls-accent-text)',
          border: 'none',
          padding: 48,
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        {content.eyebrow && (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--ls-accent-text)',
              opacity: 0.7,
            }}
          >
            {content.eyebrow}
          </span>
        )}
        <h2 className="ls-h2" style={{ color: 'var(--ls-accent-text)', whiteSpace: 'pre-wrap' }}>
          {content.headline}
        </h2>
        {content.subheadline && (
          <p
            className="ls-body"
            style={{ color: 'var(--ls-accent-text)', opacity: 0.85, maxWidth: 600 }}
          >
            {content.subheadline}
          </p>
        )}
        <div className="ls-cta-stack" style={{ justifyContent: ctaJustify(style?.ctaAlign, 'center') }}>
          <a
            href={content.primaryCtaHref ?? '#'}
            className="ls-btn"
            style={{
              background: 'var(--ls-accent-text)',
              color: 'var(--ls-accent)',
            }}
          >
            {content.primaryCta}
          </a>
          {content.secondaryCta && (
            <a
              href={content.secondaryCtaHref ?? '#'}
              className="ls-btn"
              style={{
                background: 'transparent',
                color: 'var(--ls-accent-text)',
                border: '1px solid var(--ls-accent-text)',
              }}
            >
              {content.secondaryCta}
            </a>
          )}
        </div>
      </div>
    </SectionFrame>
  );
}

export function CtaBannerImageBg({ content, style }: CB) {
  return (
    <section
      className="ls-section"
      style={{
        position: 'relative',
        background: content.image
          ? `linear-gradient(180deg, rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${content.image}) center/cover`
          : 'linear-gradient(135deg, var(--ls-accent), var(--ls-text))',
        color: '#fff',
        minHeight: 480,
      }}
    >
      <div
        className="ls-section-inner"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          alignItems: 'center',
          textAlign: 'center',
          minHeight: 480,
          justifyContent: 'center',
          maxWidth: 800,
        }}
      >
        {content.eyebrow && (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#fff',
              opacity: 0.85,
            }}
          >
            {content.eyebrow}
          </span>
        )}
        <h2
          className="ls-h1"
          style={{
            color: '#fff',
            fontSize: 'clamp(2rem, 4vw + 1rem, 4rem)',
            fontFamily: 'var(--ls-font-heading)',
            textShadow: '0 1px 12px rgba(0,0,0,0.4)',
            whiteSpace: 'pre-wrap',
          }}
        >
          {content.headline}
        </h2>
        {content.subheadline && (
          <p className="ls-body" style={{ color: 'rgba(255,255,255,0.9)' }}>
            {content.subheadline}
          </p>
        )}
        <div className="ls-cta-stack" style={{ justifyContent: ctaJustify(style?.ctaAlign, 'center') }}>
          <Btn variant="primary" href={content.primaryCtaHref ?? '#'}>
            {content.primaryCta}
          </Btn>
          {content.secondaryCta && (
            <a
              href={content.secondaryCtaHref ?? '#'}
              className="ls-btn"
              style={{
                background: 'transparent',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.5)',
              }}
            >
              {content.secondaryCta}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

export const ctaBannerVariants = {
  centered_giant: CtaBannerCenteredGiant,
  split_form: CtaBannerSplitForm,
  card_floating: CtaBannerCardFloating,
  image_bg: CtaBannerImageBg,
};
